// PURE machine contents indicators (M10 render polish, integration review: "players can read what's
// inside"): small overlays drawn on a building from its state —
//   Crate    the family icon (its highest stocked tier) on the label + the item count below it;
//   Gallery  the queued ammo (head tier) icon on the counter + the queue length, and the queue dots on the
//            front strip filled up to the queue length; during the cold open the practice tin's rounds;
//   Loader / Gunsmith   one pip per input slot at its funnel mouth, filled when the slot holds an item;
//   Smelter  an upright heat gauge (heat / heat cap of its level, a cropped bar).
// Plus the Gallery's "No ammo" state (UX §5 GalleryDry), read from the sim's readout window: `galleryDry`.
// Counts are vector digit frames (never Text); a count shows at most juice.json board.countMax (999).
import { frameId } from '../../art/catalog.ts';
import type { SimDefs } from '../../sim/defs.ts';
import type { Building, GalleryB } from '../../sim/state.ts';
import {
  COUNT_AT,
  crateIconPose,
  digitPose,
  type GeoCfg,
  galleryAmmoPose,
  heatBarPose,
  type Pose,
  QUEUE_DOTS,
  queueDotPose,
  slotPipPose,
} from './geometry.ts';

/** One overlay sprite: frame + pose; `crop` = the fill share (permille) of a cropped bar frame. */
export interface Overlay extends Pose {
  frame: string;
  crop?: number;
}

/** The digit frames of `n` (clamped to 0…board.countMax) centred on (u, v) of the building. */
function count(
  out: Overlay[],
  n: number,
  cx: number,
  cy: number,
  wu: number,
  g: GeoCfg,
  at: { x: number; y: number },
): void {
  const s = String(Math.max(0, Math.min(g.countMax, Math.floor(n))));
  for (let i = 0; i < s.length; i++) {
    out.push({
      frame: frameId.digit(s.charCodeAt(i) - 48),
      ...digitPose(cx, cy, wu, g, at.x, at.y, i, s.length),
    });
  }
}

/**
 * UX §5 GalleryDry "queue empty > 5 s → amber 'No ammo' badge": a working Gallery (gun mounted, built, not
 * paused) with nothing queued and nothing left in the practice tin, whose last closed readout seconds (sim
 * R9 window: `idle` = ticks the gun could fire with an empty queue) covering `afterMs` were idle the whole
 * ready time. Read from the state, so it is deterministic and survives a load / silent advance.
 */
export function galleryDry(b: GalleryB, d: SimDefs, afterMs: number): boolean {
  if (b.gun === null || b.ghost !== null || b.paused || b.queue.length > 0 || b.practice > 0)
    return false;
  const bucketTicks = d.economy.galleryReadout.bucketTicks;
  const need = Math.max(1, Math.ceil((afterMs * d.tickHz) / (1000 * bucketTicks)));
  const w = b.win;
  if (w.n < need) return false;
  const slots = w.ready.length;
  for (let k = 1; k <= need; k++) {
    const i = (w.idx - k + slots * need) % slots;
    const ready = w.ready[i] ?? 0;
    if (ready <= 0 || (w.idle[i] ?? 0) < ready) return false;
  }
  return true;
}

/**
 * Appends the contents overlays of `b` (world units, around its cell centre) to `out`. Ghosts show none
 * (the Ghost card lists what is delivered).
 */
export function contentsOverlays(
  b: Building,
  d: SimDefs,
  cx: number,
  cy: number,
  wu: number,
  g: GeoCfg,
  out: Overlay[],
): void {
  if (b.ghost !== null) return;
  switch (b.type) {
    case 'crate': {
      if (b.fam === null) return;
      let total = 0;
      let top = 0;
      b.counts.forEach((n, i) => {
        total += n;
        if (n > 0) top = i + 1;
      });
      if (total <= 0) return;
      out.push({ frame: frameId.item(b.fam, top), ...crateIconPose(cx, cy, wu, g) });
      count(out, total, cx, cy, wu, g, COUNT_AT.crate);
      return;
    }
    case 'gallery': {
      const head = b.queue[0];
      const n = head !== undefined ? b.queue.length : b.practice;
      if (n <= 0) return;
      const tier = head !== undefined ? head.tier : 1;
      out.push({ frame: frameId.item('ammo', tier), ...galleryAmmoPose(cx, cy, wu, g) });
      count(out, n, cx, cy, wu, g, COUNT_AT.gallery);
      const dots = Math.min(QUEUE_DOTS, b.queue.length);
      for (let i = 0; i < dots; i++)
        out.push({ frame: frameId.pip(true), ...queueDotPose(cx, cy, wu, i) });
      return;
    }
    case 'loader':
    case 'gunsmith':
      b.slots.forEach((slot, i) => {
        out.push({ frame: frameId.pip(slot.length > 0), ...slotPipPose(cx, cy, wu, g, i) });
      });
      return;
    case 'smelter': {
      const cap = d.buildings.smelter.heatCap[b.level - 1] ?? 0;
      if (cap <= 0) return;
      const fill = Math.max(0, Math.min(1000, Math.floor((b.heat * 1000) / cap)));
      const pose = heatBarPose(cx, cy, wu, g);
      out.push({ frame: frameId.barBg, ...pose });
      if (fill > 0) out.push({ frame: frameId.barFill, ...pose, crop: fill });
      return;
    }
    default:
      return;
  }
}
